﻿/**
 * UGC carousel: one initializer per [data-ugc-carousel] root.
 */
(function () {
  // One request per product row URL, shared by every UGC section on the page.
  const productRowRequests = new Map();

  function fetchProductRow(src) {
    if (!productRowRequests.has(src)) {
      const request = fetch(src, { headers: { Accept: 'text/html' }, priority: 'low' })
        .then((response) => {
          if (!response.ok) throw new Error(`ugc product row ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const row = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ugc-product-row]');
          if (!(row instanceof HTMLElement)) throw new Error('ugc product row markup missing');
          return row;
        });
      request.catch(() => productRowRequests.delete(src));
      productRowRequests.set(src, request);
    }
    return productRowRequests.get(src);
  }

  function initUgcCarousel(section) {
    if (section.dataset.ugcCarouselInit === '1') return;
    section.dataset.ugcCarouselInit = '1';
    const track = section.querySelector('[data-ugc-track]');
    const prev = section.querySelector('[data-ugc-prev]');
    const next = section.querySelector('[data-ugc-next]');
    const dots = Array.from(section.querySelectorAll('[data-ugc-dot]'));
    const sid = section.dataset.ugcSectionId;
    const modal =
      section.querySelector('[data-ugc-modal]') ||
      (sid != null && sid !== '' ? document.getElementById('ugc-modal-' + String(sid)) : null);
    const modalDialog = modal?.querySelector('.ugc-carousel__modal-dialog');
    const modalMedia = modal?.querySelector('[data-ugc-modal-media]');
    const modalVideoSlot = modal?.querySelector('[data-ugc-modal-video]');
    const modalToggle = modal?.querySelector('[data-ugc-modal-toggle]');
    const modalUnmute = modal?.querySelector('[data-ugc-modal-unmute]');
    const modalTitle = modal?.querySelector('[data-ugc-modal-title]');
    const modalCta = modal?.querySelector('[data-ugc-modal-cta]');
    const modalMute = modal?.querySelector('[data-ugc-modal-mute]');
    const modalFooter = modal?.querySelector('.ugc-carousel__modal-footer');
    const closeButtons = Array.from(modal?.querySelectorAll('[data-ugc-close]') || []);
    const playButtons = Array.from(section.querySelectorAll('[data-ugc-play]'));
    const cards = Array.from(section.querySelectorAll('.ugc-carousel__card'));
    const dotsContainer = section.querySelector('[data-ugc-dots]');
    const productGroups = Array.from(section.querySelectorAll('[data-ugc-products]'));
    const desktopSlidesPerPage = 4;
    const desktopMaxPages = 3;
    const mobileMaxVisibleDots = 5;
    let focusable = [];
    let lastFocused = null;
    let currentVideo = null;
    let lockedPage = null;
    let lockedUntil = 0;
    let lockedTargetIndex = null;

    if (!track || !modal) return;

    // Render modal at document root to avoid section stacking/overflow contexts.
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    const isDesktopViewport = () => window.matchMedia('(min-width: 990px)').matches;
    const isMobileViewport = () => window.matchMedia('(max-width: 1023px)').matches;

    const getSlideStep = () => {
      const card = track.querySelector('.ugc-carousel__card');
      if (!card) return track.clientWidth;
      return card.clientWidth + parseFloat(getComputedStyle(track).gap || 0);
    };

    const getMeasuredSlidesPerPage = () => {
      const card = track.querySelector('.ugc-carousel__card');
      if (!card) return 1;
      const cardWidth = card.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(track).gap || 0) || 0;
      if (!cardWidth) return 1;
      return Math.max(1, Math.floor((track.clientWidth + gap) / (cardWidth + gap)));
    };

    const getSlidesPerPage = () => {
      if (isDesktopViewport()) return desktopSlidesPerPage;
      return getMeasuredSlidesPerPage();
    };

    const getEffectiveSlideCount = () => {
      if (!isDesktopViewport()) return cards.length;
      return Math.min(cards.length, desktopSlidesPerPage * desktopMaxPages);
    };

    const getTotalPages = () => {
      const slidesPerPage = getSlidesPerPage();
      const totalSlides = getEffectiveSlideCount();
      return Math.max(1, Math.ceil(totalSlides / slidesPerPage));
    };

    const getMaxScrollLeft = () => {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    };

    const getPageStartIndex = (pageIndex) => {
      const slidesPerPage = getSlidesPerPage();
      const totalSlides = getEffectiveSlideCount();
      const maxStartIndex = Math.max(0, totalSlides - slidesPerPage);
      return Math.min(Math.max(0, pageIndex * slidesPerPage), maxStartIndex);
    };

    const getPageScrollLeft = (pageIndex) => {
      const targetLeft = getPageStartIndex(pageIndex) * getSlideStep();
      return Math.min(targetLeft, getMaxScrollLeft());
    };

    const getCurrentPage = () => {
      // If page is locked during smooth scroll, return locked page
      if (lockedPage !== null && performance.now() < lockedUntil) {
        return lockedPage;
      }
      const totalPages = getTotalPages();
      const currentScrollLeft = track.scrollLeft;
      let closestPage = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
        const distance = Math.abs(currentScrollLeft - getPageScrollLeft(pageIndex));
        if (distance < smallestDistance) {
          smallestDistance = distance;
          closestPage = pageIndex;
        }
      }

      return closestPage;
    };

    const getMaxVisibleDots = () => {
      if (isDesktopViewport()) return desktopMaxPages;
      if (isMobileViewport()) return mobileMaxVisibleDots;
      return dots.length;
    };

    const getDotWindow = (totalPages, currentPage, visibleDotCount) => {
      if (totalPages <= visibleDotCount) {
        return { start: 0, end: totalPages - 1 };
      }

      const side = Math.floor(visibleDotCount / 2);
      let start = currentPage - side;
      let end = start + visibleDotCount - 1;

      if (start < 0) {
        start = 0;
        end = visibleDotCount - 1;
      }

      if (end > totalPages - 1) {
        end = totalPages - 1;
        start = end - visibleDotCount + 1;
      }

      return { start, end };
    };

    const scrollToPage = (pageIndex) => {
      const totalPages = getTotalPages();
      const safePage = Math.min(totalPages - 1, Math.max(0, pageIndex));
      track.scrollTo({ left: getPageScrollLeft(safePage), behavior: 'smooth' });
    };

    const scrollByPage = (direction) => {
      scrollToPage(getCurrentPage() + direction);
    };

    const isArrowDisabled = (button) => {
      if (!button) return true;
      return button.getAttribute('aria-disabled') === 'true' || button.classList.contains('is-disabled');
    };

    const setArrowState = (button, canScroll) => {
      if (!button) return;
      const isDisabled = !canScroll;
      button.classList.toggle('is-disabled', isDisabled);
      button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      if (button.tagName === 'BUTTON') {
        button.disabled = isDisabled;
      }
    };

    const handleArrowClick = (button, direction) => (event) => {
      if (isArrowDisabled(button)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      scrollByPage(direction);
    };

    const handleArrowKeydown = (button) => (event) => {
      if (!isArrowDisabled(button)) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    if (prev) {
      prev.addEventListener('click', handleArrowClick(prev, -1));
      prev.addEventListener('keydown', handleArrowKeydown(prev));
    }
    if (next) {
      next.addEventListener('click', handleArrowClick(next, 1));
      next.addEventListener('keydown', handleArrowKeydown(next));
    }

    const setProductPagerState = (group, nextIndex) => {
      if (!group) return;

      const items = Array.from(group.querySelectorAll('[data-ugc-product-item]'));
      const paginations = Array.from(group.querySelectorAll('[data-ugc-product-pagination]'));
      const totalItems = items.length;

      if (!totalItems) return;

      const safeIndex = Math.min(totalItems - 1, Math.max(0, nextIndex));
      group.dataset.ugcProductIndex = String(safeIndex);

      items.forEach((item, index) => {
        const isActive = index === safeIndex;
        item.hidden = !isActive;
        item.classList.toggle('is-active', isActive);
      });

      paginations.forEach((pagination) => {
        const prevButton = pagination.querySelector('[data-ugc-product-prev]');
        const nextButton = pagination.querySelector('[data-ugc-product-next]');
        const pageLabel = pagination.querySelector('[data-ugc-product-page]');

        if (pageLabel) {
          pageLabel.textContent = `${safeIndex + 1}/${totalItems}`;
        }

        if (prevButton) {
          prevButton.disabled = safeIndex === 0;
          prevButton.setAttribute('aria-disabled', safeIndex === 0 ? 'true' : 'false');
        }

        if (nextButton) {
          nextButton.disabled = safeIndex === totalItems - 1;
          nextButton.setAttribute('aria-disabled', safeIndex === totalItems - 1 ? 'true' : 'false');
        }
      });
    };

    productGroups.forEach((group) => {
      const prevButtons = Array.from(group.querySelectorAll('[data-ugc-product-prev]'));
      const nextButtons = Array.from(group.querySelectorAll('[data-ugc-product-next]'));
      const getCurrentIndex = () => Number(group.dataset.ugcProductIndex || 0);

      setProductPagerState(group, 0);

      prevButtons.forEach((prevButton) => {
        prevButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          setProductPagerState(group, getCurrentIndex() - 1);
        });
      });

      nextButtons.forEach((nextButton) => {
        nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          setProductPagerState(group, getCurrentIndex() + 1);
        });
      });
    });

    // Product rows render as shells: the section never loads a product, since
    // Shopify resolves product settings before a section renders (the
    // homepage's 11 UGC products cost ~3s of server time). The rows are filled
    // from sections/ugc-product-row.liquid once the carousel nears the
    // viewport. Override name, colour and image are server-rendered and kept.
    const productRows = Array.from(section.querySelectorAll('[data-ugc-product-item][data-ugc-product-src]'));

    const fillProductRow = (item, row) => {
      const title = row.dataset.ugcTitle || '';
      const subtitle = row.dataset.ugcSubtitle || '';
      const titleEl = item.querySelector('[data-ugc-product-title]');
      const subtitleEl = item.querySelector('[data-ugc-product-subtitle]');
      const imageLink = item.querySelector('[data-ugc-product-image-link]');

      if (titleEl && !item.hasAttribute('data-ugc-keep-title')) {
        titleEl.textContent = title;
      }
      if (subtitleEl && !item.hasAttribute('data-ugc-keep-subtitle')) {
        subtitleEl.textContent = subtitle;
        subtitleEl.hidden = !subtitle;
      }

      const shownTitle = (titleEl ? titleEl.textContent : title).trim();
      if (imageLink && !item.hasAttribute('data-ugc-keep-image')) {
        const image = row.querySelector('img');
        if (image) {
          const clone = /** @type {HTMLImageElement} */ (document.importNode(image, true));
          clone.alt = shownTitle;
          imageLink.replaceChildren(clone);
        } else {
          imageLink.remove();
        }
      } else if (imageLink) {
        const overrideImage = imageLink.querySelector('img');
        if (overrideImage && !overrideImage.getAttribute('alt')) overrideImage.alt = shownTitle;
      }

      // The slide's first product names the video modal and the media's alt
      // text, as the server-rendered product title did before.
      const card = item.closest('.ugc-carousel__card');
      if (card && title && card.hasAttribute('data-ugc-title-pending') && card.querySelector('[data-ugc-product-item]') === item) {
        card.removeAttribute('data-ugc-title-pending');
        card.querySelectorAll('[data-ugc-play]').forEach((button) => button.setAttribute('data-ugc-title', title));
        card.querySelectorAll('img.ugc-carousel__image').forEach((image) => {
          image.alt = title;
        });
      }
    };

    const loadProductRows = () => {
      productRows.forEach((item) => {
        const src = item.dataset.ugcProductSrc;
        if (!src || item.dataset.ugcProductState !== 'idle') return;
        item.dataset.ugcProductState = 'loading';
        fetchProductRow(src)
          .then((row) => {
            fillProductRow(item, row);
            item.dataset.ugcProductState = 'ready';
          })
          .catch(() => {
            // Keep the row useful: a plain link to the product.
            item.dataset.ugcProductState = 'error';
            const titleEl = item.querySelector('[data-ugc-product-title]');
            if (titleEl && !titleEl.textContent.trim()) titleEl.textContent = 'Shop this product';
            if (!item.hasAttribute('data-ugc-keep-image')) {
              item.querySelector('[data-ugc-product-image-link]')?.remove();
            }
          });
      });
    };

    if (productRows.length) {
      if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
        loadProductRows();
      } else {
        const productRowObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            productRowObserver.disconnect();
            if (typeof window.requestIdleCallback === 'function') {
              window.requestIdleCallback(loadProductRows, { timeout: 1500 });
            } else {
              window.setTimeout(loadProductRows, 200);
            }
          },
          { rootMargin: '0px 0px 1500px 0px' }
        );
        productRowObserver.observe(section);
      }
    }

    const updateDots = () => {
      if (!dots.length || !dotsContainer) return;

      const totalPages = getTotalPages();
      if (totalPages <= 1) {
        dotsContainer.hidden = true;
        dots.forEach((dot) => {
          dot.hidden = true;
          dot.classList.remove('is-active', 'is-edge');
          dot.setAttribute('aria-current', 'false');
        });
        return;
      }

      dotsContainer.hidden = false;
      const currentPage = getCurrentPage();
      const visibleDotCount = Math.min(totalPages, getMaxVisibleDots());
      const { start } = getDotWindow(totalPages, currentPage, visibleDotCount);

      dots.forEach((dot, i) => {
        if (i >= visibleDotCount) {
          dot.hidden = true;
          dot.classList.remove('is-active', 'is-edge');
          dot.setAttribute('aria-current', 'false');
          return;
        }

        const pageIndex = start + i;
        const active = pageIndex === currentPage;
        const isEdge = totalPages > visibleDotCount && (pageIndex === start || pageIndex === start + visibleDotCount - 1);

        dot.hidden = false;
        dot.dataset.ugcPage = String(pageIndex);
        dot.setAttribute('aria-label', `Go to slide ${pageIndex + 1}`);
        dot.classList.toggle('is-active', active);
        dot.classList.toggle('is-edge', isEdge);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const pageIndex = Number(dot.dataset.ugcPage || 0);
        // Lock the page during smooth scroll (800ms)
        lockedPage = pageIndex;
        lockedTargetIndex = getPageStartIndex(pageIndex);
        lockedUntil = performance.now() + 800;
        // Immediately update dots to show selected page highlighted
        updateDots();
        // Then scroll to target
        scrollToPage(pageIndex);
      });
    });

    const updateArrows = () => {
      if (!prev && !next) return;
      const totalPages = getTotalPages();
      if (totalPages <= 1) {
        setArrowState(prev, false);
        setArrowState(next, false);
        return;
      }
      const currentPage = getCurrentPage();
      setArrowState(prev, currentPage > 0);
      setArrowState(next, currentPage < totalPages - 1);
    };

    const syncCarouselState = () => {
      updateDots();
      updateArrows();
    };

    track.addEventListener('scroll', () => {
      window.requestAnimationFrame(syncCarouselState);
    });

    window.addEventListener('resize', () => {
      // Clear lock on resize as slides per page may change
      lockedPage = null;
      lockedUntil = 0;
      lockedTargetIndex = null;
    });

    const buildEmbedUrl = (url) => {
      const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
      if (youtubeMatch) {
        return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&playsinline=1&rel=0&mute=1`;
      }
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&playsinline=1&muted=1`;
      }
      return url;
    };

    const setModalMeta = (trigger) => {
      if (!trigger) return;
      if (modalTitle) {
        modalTitle.textContent = trigger.getAttribute('data-ugc-title') || '';
      }
      if (modalCta) {
        const hasLink = trigger.getAttribute('data-ugc-has-link') === 'true';
        const link = trigger.getAttribute('data-ugc-link');
        if (hasLink && link) {
          modalCta.hidden = false;
          modalCta.setAttribute('href', link);
        } else {
          modalCta.hidden = true;
          modalCta.removeAttribute('href');
        }
      }
    };

    const setModalAspect = (trigger, width = 0, height = 0) => {
      if (!modal || !trigger) return;
      const prefersPortrait = width > 0 && height > 0
        ? height > width
        : trigger.getAttribute('data-ugc-portrait') === 'true';
      modal.classList.toggle('ugc-carousel__modal--portrait', prefersPortrait);
      if (width > 0 && height > 0) {
        modal.style.setProperty('--ugc-modal-media-ratio', `${width} / ${height}`);
      } else {
        modal.style.removeProperty('--ugc-modal-media-ratio');
      }
    };

    const setModalMediaBounds = (ratioWidth = 0, ratioHeight = 0) => {
      if (!modal || !modalDialog || !modalMedia) return;
      modalMedia.style.removeProperty('width');
      modalMedia.style.removeProperty('height');
      modalDialog.style.removeProperty('width');

      if (!(ratioWidth > 0 && ratioHeight > 0)) return;

      const maxDialogWidth = modal.classList.contains('ugc-carousel__modal--portrait')
        ? Math.min(window.innerWidth * 0.92, 520)
        : Math.min(window.innerWidth * 0.96, 1040);
      const maxDialogHeight = Math.min(window.innerHeight * 0.92, 960);
      const footerHeight = modalFooter && getComputedStyle(modalFooter).display !== 'none'
        ? modalFooter.offsetHeight
        : 0;
      const availableHeight = Math.max(maxDialogHeight - footerHeight, 0);
      const ratio = ratioWidth / ratioHeight;
      let mediaWidth = Math.min(maxDialogWidth, availableHeight * ratio);
      let mediaHeight = mediaWidth / ratio;

      if (mediaHeight > availableHeight) {
        mediaHeight = availableHeight;
        mediaWidth = mediaHeight * ratio;
      }

      modalMedia.style.width = `${mediaWidth}px`;
      modalMedia.style.height = `${mediaHeight}px`;
      modalDialog.style.width = `${mediaWidth}px`;
    };

    const resetModalState = () => {
      if (!modal) return;
      modal.classList.remove('is-playing', 'is-muted', 'is-iframe');
      modal.style.removeProperty('--ugc-modal-media-ratio');
      if (modalMedia) {
        modalMedia.style.removeProperty('width');
        modalMedia.style.removeProperty('height');
      }
      if (modalDialog) {
        modalDialog.style.removeProperty('width');
      }
      setPlayingUI(false);
      if (modalMute) {
        modalMute.hidden = false;
        modalMute.setAttribute('aria-label', 'Unmute video');
      }
    };

    const updateMuteUI = (isMuted) => {
      if (!modal) return;
      modal.classList.toggle('is-muted', isMuted);
      if (modalMute) {
        modalMute.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
      }
    };

    const setPlayingUI = (isPlaying) => {
      if (!modal) return;
      modal.classList.toggle('is-playing', isPlaying);
      if (modalToggle) {
        modalToggle.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      }
    };

    const unmuteVideo = () => {
      if (!currentVideo || !modal) return;
      currentVideo.muted = false;
      updateMuteUI(false);
    };

    const togglePlayback = () => {
      if (!currentVideo) return;
      if (currentVideo.paused) {
        currentVideo.play().catch(() => {});
      } else {
        currentVideo.pause();
      }
    };

    const openModal = (card, trigger) => {
      if (!modal || !modalMedia || !modalVideoSlot) return;
      const data = card.querySelector('.ugc-carousel__video-data');
      if (!data) return;

      const file = data.querySelector('[data-video-file]')?.getAttribute('data-video-file');
      const url = data.querySelector('[data-video-url]')?.getAttribute('data-video-url');

      let media = '';
      let isIframe = false;
      if (file) {
        media = `<video playsinline muted preload="metadata" data-ugc-player><source src="${file}" type="video/mp4"></video>`;
      } else if (url) {
        const isYoutube = /youtube\.com|youtu\.be/.test(url);
        const isVimeo = /vimeo\.com/.test(url);
        if (isYoutube || isVimeo) {
          media = `<iframe src="${buildEmbedUrl(url)}" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
          isIframe = true;
        } else {
          media = `<video playsinline muted preload="metadata" data-ugc-player><source src="${url}" type="video/mp4"></video>`;
        }
      }

      if (!media) return;
      modalVideoSlot.innerHTML = media;
      currentVideo = modalVideoSlot.querySelector('video');
      resetModalState();
      setModalMeta(trigger);
      setModalAspect(trigger);
      if (isIframe) {
        modal.classList.add('is-iframe');
        setModalMediaBounds(
          modal.classList.contains('ugc-carousel__modal--portrait') ? 9 : 16,
          modal.classList.contains('ugc-carousel__modal--portrait') ? 16 : 9
        );
        if (modalMute) {
          modalMute.hidden = true;
        }
      } else if (currentVideo) {
        currentVideo.controls = false;
        currentVideo.muted = true;
        currentVideo.load();
        updateMuteUI(true);
        currentVideo.addEventListener('loadedmetadata', () => {
          setModalAspect(trigger, currentVideo.videoWidth, currentVideo.videoHeight);
          setModalMediaBounds(currentVideo.videoWidth, currentVideo.videoHeight);
        });
        currentVideo.addEventListener('play', () => setPlayingUI(true));
        currentVideo.addEventListener('pause', () => setPlayingUI(false));
        currentVideo.addEventListener('ended', () => setPlayingUI(false));
        currentVideo.addEventListener('volumechange', () => {
          updateMuteUI(currentVideo.muted);
        });
        setPlayingUI(!currentVideo.paused);
        currentVideo.play().catch(() => {});
      }
      modal.classList.add('is-active');
      modal.setAttribute('aria-hidden', 'false');
      lastFocused = trigger || document.activeElement;
      focusable = Array.from(modal.querySelectorAll('button, [href], iframe, video')).filter(
        (el) => !el.hasAttribute('hidden')
      );
      focusable[0]?.focus();
      document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
      if (!modal || !modalVideoSlot) return;
      modal.classList.remove('is-active');
      modal.setAttribute('aria-hidden', 'true');
      if (currentVideo) {
        currentVideo.pause();
        currentVideo.removeAttribute('src');
      }
      modalVideoSlot.innerHTML = '';
      currentVideo = null;
      resetModalState();
      document.body.style.overflow = '';
      if (lastFocused) lastFocused.focus();
    };

    closeButtons.forEach((button) => button.addEventListener('click', closeModal));

    document.addEventListener('keydown', (event) => {
      if (!modal || modal.getAttribute('aria-hidden') === 'true') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      }
      if (event.key === 'Tab' && focusable.length) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    window.addEventListener('resize', () => {
      if (!modal || !modal.classList.contains('is-active')) return;
      if (currentVideo && currentVideo.videoWidth > 0 && currentVideo.videoHeight > 0) {
        setModalMediaBounds(currentVideo.videoWidth, currentVideo.videoHeight);
        return;
      }
      if (modal.classList.contains('is-iframe')) {
        setModalMediaBounds(
          modal.classList.contains('ugc-carousel__modal--portrait') ? 9 : 16,
          modal.classList.contains('ugc-carousel__modal--portrait') ? 16 : 9
        );
      }
    });

    playButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const media = button.closest('.ugc-carousel__media');
        if (!media) return;
        const video = media.querySelector('[data-ugc-inline-video]');
        const controls = media.querySelector('[data-ugc-inline-controls]');
        const muteBtn = media.querySelector('[data-ugc-inline-mute]');
        const pauseBtn = media.querySelector('[data-ugc-inline-pause]');
        if (!video) return;

        const syncMuteState = () => {
          if (!muteBtn) return;
          muteBtn.classList.toggle('is-unmuted', !video.muted);
          muteBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
        };

        const syncPlaybackState = () => {
          if (!pauseBtn) return;
          pauseBtn.classList.toggle('is-paused', video.paused);
          pauseBtn.setAttribute('aria-label', video.paused ? 'Play' : 'Pause');
        };

        const toggleInlinePlayback = () => {
          if (video.paused) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        };

        if (video.dataset.ugcInlineInitialized !== 'true') {
          video.dataset.ugcInlineInitialized = 'true';

          video.addEventListener('play', syncPlaybackState);
          video.addEventListener('pause', syncPlaybackState);
          video.addEventListener('ended', () => {
            video.currentTime = 0;
            syncPlaybackState();
          });
          video.addEventListener('volumechange', syncMuteState);
          video.addEventListener('click', toggleInlinePlayback);

          if (muteBtn) {
            muteBtn.addEventListener('click', () => {
              video.muted = !video.muted;
              syncMuteState();
            });
          }

          if (pauseBtn) {
            pauseBtn.addEventListener('click', () => {
              toggleInlinePlayback();
            });
          }
        }

        // Hide center play button, show video, and reveal play/pause after first interaction.
        button.hidden = true;
        video.hidden = false;
        if (controls) controls.hidden = false;
        if (pauseBtn) pauseBtn.hidden = false;

        if (!video.dataset.ugcInlineLoaded) {
          video.load();
          video.dataset.ugcInlineLoaded = 'true';
        }

        video.play().catch(() => {});
        syncMuteState();
        syncPlaybackState();
      });
    });

    if (modalToggle) {
      modalToggle.addEventListener('click', () => {
        if (!currentVideo) return;
        togglePlayback();
      });
    }

    if (modalUnmute) {
      modalUnmute.addEventListener('click', (event) => {
        event.stopPropagation();
        unmuteVideo();
      });
    }

    if (modalMedia) {
      modalMedia.addEventListener('click', (event) => {
        if (!currentVideo) return;
        if (event.target.tagName === 'VIDEO') {
          togglePlayback();
        }
      });
    }

    if (modalMute) {
      modalMute.addEventListener('click', () => {
        if (!currentVideo) return;
        currentVideo.muted = !currentVideo.muted;
        updateMuteUI(currentVideo.muted);
      });
    }

    // Kick off the first visible page of images as soon as the section is
    // within ~2000px of the viewport, with the lead image at high priority.
    // The margin has to exceed Chrome's own lazy-load lookahead (~1250px on
    // 4G) or native loading starts first and this becomes a no-op. Offscreen
    // slides (right of the first page, or sections still far away) keep
    // native lazy loading.
    const primeLeadImages = () => {
      const count = Math.max(1, getSlidesPerPage());
      cards.slice(0, count).forEach((card, index) => {
        const image = card.querySelector('img.ugc-carousel__image');
        // A lazy image that hasn't started loading still reports complete=true
        // in Chrome — only skip images that actually have pixels.
        if (!image || (image.complete && image.naturalWidth > 0)) return;
        if (index === 0) image.setAttribute('fetchpriority', 'high');
        image.setAttribute('loading', 'eager');
      });
    };

    if ('IntersectionObserver' in window) {
      const leadImageObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          leadImageObserver.disconnect();
          primeLeadImages();
        },
        { rootMargin: '0px 0px 2000px 0px' }
      );
      leadImageObserver.observe(section);
    } else {
      primeLeadImages();
    }

    syncCarouselState();

    window.addEventListener('resize', () => {
      window.requestAnimationFrame(syncCarouselState);
    });

    window.addEventListener('load', () => {
      window.requestAnimationFrame(syncCarouselState);
    });

    track.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', syncCarouselState, { once: true });
      image.addEventListener('error', syncCarouselState, { once: true });
    });

  }

  function bootUgcCarousels() {
    document.querySelectorAll('[data-ugc-carousel]').forEach(initUgcCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootUgcCarousels);
  } else {
    bootUgcCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!target || !target.querySelectorAll) return;
    target.querySelectorAll('[data-ugc-carousel]').forEach(initUgcCarousel);
    if (target.matches && target.matches('[data-ugc-carousel]')) {
      initUgcCarousel(target);
    }
  });
})();

